import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ViewEncapsulation,
  OnInit,
  OnChanges,
  TemplateRef,
} from '@angular/core';
import { PhxTimePickerFormat, PhxTimePickerTypes } from './phx-time-picker.type';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';

@Component({
  selector: 'phx-time-picker-panel',
  templateUrl: './phx-time-picker-panel.component.html',
  styleUrls: ['./phx-time-picker-panel.component.scss'],
  host: {
    class: 'phx-time-picker-panel',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PhxTimePickerPanelComponent implements OnInit, OnChanges {
  @Input() phxInputSuffix: TemplateRef<any>;
  @Input() phxTimePickerFormat: PhxTimePickerFormat = 'default';
  @Input() phxValue?: Date;
  @Input() phxShowInput: boolean = false;
  @Input() phxDisableBefore?: Date;
  @Input() phxDisableAfter?: Date;
  @Input() phxDisabledHours: number[] = [];
  @Input() phxDisabledMinutes: number[] = [];
  @Input() phxIsDropdownOpen: boolean = false;
  @Output() phxValueChange: EventEmitter<Date> = new EventEmitter<Date>();

  public hoursArray: string[] = [];
  public minutesArray: string[] = [];
  public amPmArray: string[] = ['AM', 'PM'];
  public selectedHour: string = '12';
  public selectedMinute: string = '00';
  public selectedAmPm: string = 'AM';

  @ViewChildren('hourBox') hourBoxes!: QueryList<ElementRef>;
  @ViewChildren('minuteBox') minuteBoxes!: QueryList<ElementRef>;
  @ViewChildren('ampmBox') ampmBoxes!: QueryList<ElementRef>;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.initializeTimeArrays();
    this.updateSelectedTime(this.phxValue);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.phxValue || changes.phxTimePickerFormat) {
      this.initializeTimeArrays();
      this.updateSelectedTime(this.phxValue);
    }
    if (changes.phxIsDropdownOpen) {
      if (this.phxIsDropdownOpen) {
        this.scrollTimeIntoView();
      }
    }
  }

  public updateSelectedTime(date?: Date): void {
    if (date instanceof Date && !isNaN(date.getTime())) {
      date = new Date(date);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      if (this.phxTimePickerFormat === '24H') {
        this.selectedHour = hours.toString().padStart(2, '0');
      } else {
        this.selectedHour = (hours % 12 || 12).toString().padStart(2, '0');
        this.selectedAmPm = hours >= 12 ? 'PM' : 'AM';
      }
      this.selectedMinute = minutes.toString().padStart(2, '0');
    } else {
      this.clearTime();
    }
  }

  public onTimeInputChange(date: Date): void {
    this.updateSelectedTime(date);
    this.phxValueChange.emit(date);
  }

  public handleKeydown(event: KeyboardEvent, index: number, type: PhxTimePickerTypes): void {
    let elements: ElementRef[];
    switch (type) {
      case 'hour':
        elements = this.hourBoxes.toArray();
        break;
      case 'minute':
        elements = this.minuteBoxes.toArray();
        break;
      case 'ampm':
        elements = this.ampmBoxes.toArray();
        break;
      default:
        return;
    }
    // TODO: FP-2741 handle keyboard navigation
    const focusNextElement = (currentIndex: number, direction: number) => {
      let nextIndex = (currentIndex + direction + elements.length) % elements.length;
      let iterations = 0;
      while (elements[nextIndex].nativeElement.classList.contains('phx-time-box-disabled') && iterations < elements.length) {
        nextIndex = (nextIndex + direction + elements.length) % elements.length;
        iterations++;
      }
      if (iterations < elements.length) {
        elements[nextIndex].nativeElement.focus();
      }
    };

    switch (event.key) {
      case KeyboardKeys.ARROW_UP:
        event.preventDefault();
        focusNextElement(index, 1);
        break;
      case KeyboardKeys.ARROW_DOWN:
        event.preventDefault();
        focusNextElement(index, -1);
        break;
      case KeyboardKeys.ARROW_RIGHT:
        event.preventDefault();
        if (type === 'hour') {
          elements = this.minuteBoxes.toArray();
          if (elements.length > 0) {
            elements[0].nativeElement.focus();
          }
        } else if (type === 'minute') {
          if (this.phxTimePickerFormat === 'default') {
            elements = this.ampmBoxes.toArray();
            if (elements.length > 0) {
              elements[0].nativeElement.focus();
            }
          }
        } else if (type === 'ampm') {
          elements = this.hourBoxes.toArray();
          if (elements.length > 0) {
            elements[0].nativeElement.focus();
          }
        }
        break;
      case KeyboardKeys.ARROW_LEFT:
        event.preventDefault();
        if (type === 'ampm') {
          elements = this.minuteBoxes.toArray();
          if (elements.length > 0) {
            elements[elements.length - 1].nativeElement.focus();
          }
        } else if (type === 'minute') {
          elements = this.hourBoxes.toArray();
          if (elements.length > 0) {
            elements[elements.length - 1].nativeElement.focus();
          }
        } else if (type === 'hour') {
          if (this.phxTimePickerFormat === 'default') {
            elements = this.ampmBoxes.toArray();
            if (elements.length > 0) {
              elements[elements.length - 1].nativeElement.focus();
            }
          }
        }
        break;
      case KeyboardKeys.ENTER:
      case KeyboardKeys.SPACE:
        event.preventDefault();
        const selectedText = elements[index].nativeElement.innerText.trim();
        if (!elements[index].nativeElement.classList.contains('phx-time-box-disabled')) {
          if (type === 'hour') {
            this.selectHour(selectedText);
          } else if (type === 'minute') {
            this.selectMinute(selectedText);
          } else if (type === 'ampm') {
            this.selectAmPm(selectedText);
          }
        }
        break;
      default:
        break;
    }
  }

  private initializeTimeArrays(): void {
    this.hoursArray = this.generateHoursArray();
    this.minutesArray = this.generateMinutesArray();
  }

  private generateHoursArray(): string[] {
    const hours: string[] = [];
    if (this.phxTimePickerFormat === '24H') {
      for (let hour = 0; hour < 24; hour++) {
        hours.push(hour.toString().padStart(2, '0'));
      }
    } else {
      for (let hour = 1; hour <= 12; hour++) {
        hours.push(hour.toString().padStart(2, '0'));
      }
    }
    return hours;
  }

  private generateMinutesArray(): string[] {
    const minutes: string[] = [];
    for (let minute = 0; minute < 60; minute++) {
      minutes.push(minute.toString().padStart(2, '0'));
    }
    return minutes;
  }

  private parseTimeStringToDate(time: string): Date | null {
    if (!time) return null;
    let hours = 0;
    let minutes = 0;
    let isPM = false;

    if (this.phxTimePickerFormat === '24H') {
      const timeParts = time.split(':');
      if (timeParts.length !== 2) return null;
      hours = parseInt(timeParts[0], 10);
      minutes = parseInt(timeParts[1], 10);
    } else {
      // 'default'
      const regex = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i;
      const match = time.match(regex);
      if (!match) return null;
      hours = parseInt(match[1], 10);
      minutes = parseInt(match[2], 10);
      isPM = match[3].toUpperCase() === 'PM';
      if (isPM && hours < 12) {
        hours += 12;
      } else if (!isPM && hours === 12) {
        hours = 0;
      }
    }

    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  public isHourDisabled(hour: string): boolean {
    const hourNum = parseInt(hour, 10);
    let hour24 = hourNum;

    if (this.phxTimePickerFormat !== '24H') {
      // For 12-hour format, convert to 24-hour based on the current time's AM/PM
      // instead of the selectedAmPm which changes with selection
      hour24 = this.convertHourTo24(hourNum, this.selectedAmPm);
    }

    // Check if hour is in the disabled hours array - this should remain constant
    // regardless of what time is selected
    if (this.phxDisabledHours && this.phxDisabledHours.includes(hourNum)) {
      return true;
    }

    // If there's a disable-before time, any hour strictly less than that hour is disabled.
    if (this.phxDisableBefore && hour24 < this.phxDisableBefore.getHours()) {
      return true;
    }

    // If there's a disable-after time, any hour strictly greater than that hour is disabled.
    if (this.phxDisableAfter && hour24 > this.phxDisableAfter.getHours()) {
      return true;
    }

    return false;
  }

  public isMinuteDisabled(minute: string): boolean {
    const minuteNum = parseInt(minute, 10);
    const hourNum = parseInt(this.selectedHour, 10);

    let hour24 = hourNum;
    if (this.phxTimePickerFormat !== '24H') {
      hour24 = this.convertHourTo24(hourNum, this.selectedAmPm);
    }

    // Check if minute is in the disabled minutes array - this should remain constant
    // regardless of what time is selected
    if (this.phxDisabledMinutes && this.phxDisabledMinutes.includes(minuteNum)) {
      return true;
    }

    // Time range checks
    // If the hour is completely outside allowed range, minutes are irrelevant.
    if (this.phxDisableBefore && hour24 < this.phxDisableBefore.getHours()) {
      return true;
    }
    if (this.phxDisableAfter && hour24 > this.phxDisableAfter.getHours()) {
      return true;
    }

    // If we are at the same hour as phxDisableBefore, minutes before that time are disabled.
    if (this.phxDisableBefore && hour24 === this.phxDisableBefore.getHours() && minuteNum < this.phxDisableBefore.getMinutes()) {
      return true;
    }

    // If we are at the same hour as phxDisableAfter, minutes after that time are disabled.
    if (this.phxDisableAfter && hour24 === this.phxDisableAfter.getHours() && minuteNum > this.phxDisableAfter.getMinutes()) {
      return true;
    }

    return false;
  }

  public isAmPmDisabled(date: string): boolean {
    // If phxDisableBefore is in the PM, then all AM times are before it and may be disabled.
    if (this.phxDisableBefore && this.phxDisableBefore.getHours() >= 12 && date === 'AM') {
      return true;
    }

    // If phxDisableAfter is in the AM, then all PM times are after it and may be disabled.
    if (this.phxDisableAfter && this.phxDisableAfter.getHours() < 12 && date === 'PM') {
      return true;
    }

    return false;
  }

  private convertHourTo24(hour: number, amPm: string): number {
    if (!amPm) return hour; // Handle null amPm

    if (amPm === 'PM' && hour < 12) {
      return hour + 12;
    } else if (amPm === 'AM' && hour === 12) {
      return 0;
    }
    return hour;
  }

  public onHourChange(hour: string): void {
    this.selectedHour = hour;
    this.emitTimeChange();
  }

  public onMinuteChange(minute: string): void {
    this.selectedMinute = minute;
    this.emitTimeChange();
  }

  public onAmPmChange(amPm: string): void {
    this.selectedAmPm = amPm;
    this.emitTimeChange();
  }

  private emitTimeChange(): void {
    let timeString: string;
    if (this.phxTimePickerFormat === '24H') {
      timeString = `${this.selectedHour}:${this.selectedMinute}`;
    } else {
      timeString = `${this.selectedHour}:${this.selectedMinute} ${this.selectedAmPm}`;
    }
    const date = this.parseTimeStringToDate(timeString);
    if (date) {
      this.phxValueChange.emit(date);
    }
  }

  public selectHour(hour: string): void {
    this.selectedHour = hour;
    this.emitTimeChange();
  }

  public selectMinute(minute: string): void {
    this.selectedMinute = minute;
    this.emitTimeChange();
  }

  public selectAmPm(amPm: string): void {
    this.selectedAmPm = amPm;
    this.emitTimeChange();
  }

  public clearTime(): void {
    this.phxValue = null;
    this.selectedAmPm = null;
    this.selectedMinute = null;
    this.selectedHour = null;
  }

  public scrollTimeIntoView(): void {
    this.scrollColumnToSelected(this.hourBoxes, this.selectedHour);
    this.scrollColumnToSelected(this.minuteBoxes, this.selectedMinute);
  }

  private scrollColumnToSelected(boxes: QueryList<ElementRef>, selectedValue: string): void {
    const items = boxes?.toArray();
    if (!items?.length) {
      return;
    }
    const selectedIndex = items.findIndex(
      (el) => el.nativeElement.innerText.trim() === selectedValue
    );
    if (selectedIndex < 0) {
      return;
    }
    const el = items[selectedIndex].nativeElement;
    const container = el.parentElement;
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      container.scrollTop += elRect.top - containerRect.top;
    }
  }
}
